import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';
import { Typography } from '@mui/material';
import { IconButton } from '@mui/material';
import { Paper } from '@mui/material';

export interface ToastProps {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  description?: string;
  duration?: number;
  onClose: (id: string) => void;
}

const toastStyles = {
  success: {
    bar: 'success.light',
    icon: <CheckCircleIcon color="success" />,
  },
  error: {
    bar: 'error.light',
    icon: <ErrorIcon color="error" />,
  },
  warning: {
    bar: 'warning.light',
    icon: <WarningIcon color="warning" />,
  },
  info: {
    bar: 'info.light',
    icon: <InfoIcon color="info" />,
  },
};

export const Toast: React.FC<ToastProps> = ({ id, type, title, description, onClose }) => {
  const style = toastStyles[type];

  return (
    <Paper
      elevation={8}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        borderRadius: '4px',
        width: '100%',
        maxWidth: {
          xs: '90vw', // Small screens
          sm: '500px', // Default
        },
        overflow: 'hidden',
        '@keyframes slideIn': {
          from: {
            transform: 'translateX(-100%)',
            opacity: 0,
          },
          to: {
            transform: 'translateX(0)',
            opacity: 1,
          },
        },
        animation: 'slideIn 0.3s ease-out',
      }}
    >
      {/* Color Bar with Icon */}
      <Box
        sx={{
          backgroundColor: style.bar,
          position: 'relative',
          width: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
          }}
        >
          {style.icon}
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          gap: '16px',
          alignItems: 'start',
          padding: '16px',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '18px',
              wordBreak: 'break-word',
            }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: '16px',
                wordBreak: 'break-word',
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {/* Close Button */}
        <IconButton
          onClick={() => onClose(id)}
          aria-label="Close"
          size="small"
          sx={{
            width: '24px',
            height: '24px',
          }}
        >
          <CloseIcon sx={{ fontSize: '20px' }} />
        </IconButton>
      </Box>
    </Paper>
  );
};
